/**
 * HumanizationConfigTab Component
 * Configure humanization settings for making AI responses more natural
 */

import { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  IconSparkles,
  IconMessage,
  IconMoodSmile,
  IconMessages,
  IconRobot,
  IconPencil,
  IconMusic,
  IconBolt,
  IconInfoCircle,
  IconClock,
} from '@tabler/icons-react';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import './HumanizationConfigTab.css';

// ===============================================
// DEFAULT HUMANIZATION CONFIG
// ===============================================

const DEFAULT_CONFIG = {
  enabled: true,
  modules: {
    anti_robot_filter: { enabled: true },
    casual_writing: { enabled: true, typo_enabled: false, typo_rate: 0.0 },
    relaxed_tone: { enabled: true },
    quick_responses: { enabled: true },
    avoid_closing_questions: { enabled: false },
    message_splitter: { enabled: true, max_messages: 3, message_delays: [] },
    natural_emojis: { enabled: true, probability: 0.3, max_per_interaction: 2 },
    conversation_flow: { enabled: true },
  },
};

// ===============================================
// MODULE INFO
// ===============================================

const MODULE_INFO = {
  anti_robot_filter: {
    name: 'Filtro Anti-Robô',
    description: 'Evita frases corporativas e robóticas como "Prezado(a)" ou "Estou à disposição"',
    icon: IconRobot,
    category: 'prompt',
  },
  casual_writing: {
    name: 'Escrita Casual',
    description: 'Usa linguagem informal como "vc", "tb", "pq", "tá"',
    icon: IconPencil,
    category: 'prompt',
  },
  relaxed_tone: {
    name: 'Tom Descontraído',
    description: 'Mantém um tom amigável e acolhedor, como conversar com um conhecido',
    icon: IconMoodSmile,
    category: 'prompt',
  },
  quick_responses: {
    name: 'Respostas Diretas',
    description: 'Respostas curtas e objetivas para perguntas simples',
    icon: IconBolt,
    category: 'prompt',
  },
  avoid_closing_questions: {
    name: 'Evitar Perguntas no Final',
    description: 'Reduz a chance de que a IA finalize respostas com perguntas como "Posso ajudar?" ou "Quer agendar?"',
    icon: IconMessage,
    category: 'prompt',
  },
  message_splitter: {
    name: 'Divisor de Mensagens',
    description: 'Divide respostas longas em múltiplas mensagens curtas',
    icon: IconMessages,
    category: 'post',
  },
  natural_emojis: {
    name: 'Emojis Naturais',
    description: 'Adiciona emojis contextuais de forma natural (máximo 1-2 por resposta)',
    icon: IconMoodSmile,
    category: 'post',
  },
  conversation_flow: {
    name: 'Fluxo de Conversa',
    description: 'Define pontos de pausa e transições naturais na conversa',
    icon: IconMusic,
    category: 'post',
  },
};

// ===============================================
// MODULE CARD
// ===============================================

const ModuleCard = ({ moduleKey, config, onChange }) => {
  const info = MODULE_INFO[moduleKey];
  if (!info) return null;

  const Icon = info.icon;
  const isEnabled = config?.enabled !== false;

  const handleToggle = () => {
    onChange(moduleKey, { ...config, enabled: !isEnabled });
  };

  return (
    <div className={`hum-module ${isEnabled ? '' : 'hum-module--disabled'}`}>
      <div className="hum-module__main">
        <div className={`hum-module__icon ${isEnabled ? 'hum-module__icon--active' : ''}`}>
          <Icon size={18} />
        </div>
        <div className="hum-module__info">
          <div className="hum-module__header">
            <span className="hum-module__name">{info.name}</span>
          </div>
          <span className="hum-module__desc">{info.description}</span>
        </div>
        <label className="hum-toggle">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={handleToggle}
          />
          <span className="hum-toggle__slider" />
        </label>
      </div>

      {/* Message Splitter settings */}
      {isEnabled && moduleKey === 'message_splitter' && (
        <div className="hum-module__settings">
          <div className="hum-slider">
            <span className="hum-slider__label">Máximo de mensagens</span>
            <div className="hum-slider__control">
              <input
                type="range"
                className="hum-slider__input"
                min={2}
                max={4}
                value={config?.max_messages || 3}
                onChange={(e) => {
                  const newMax = parseInt(e.target.value);
                  const delays = config?.message_delays || [];
                  const adjustedDelays = delays.slice(0, newMax);
                  onChange(moduleKey, { ...config, max_messages: newMax, message_delays: adjustedDelays });
                }}
              />
              <span className="hum-slider__value">{config?.max_messages || 3}</span>
            </div>
          </div>

          {/* Per-message delay configuration */}
          <div className="hum-delays">
            <div className="hum-delays__header">
              <IconClock size={14} />
              <span className="hum-delays__title">Delay por mensagem</span>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Configure o tempo de espera antes de enviar cada mensagem. Use 0 para delay automático.</Tooltip>}
              >
                <IconInfoCircle size={13} className="hum-delays__info" />
              </OverlayTrigger>
            </div>
            <div className="hum-delays__list">
              {Array.from({ length: config?.max_messages || 3 }, (_, i) => {
                const delays = config?.message_delays || [];
                const delayMs = delays[i] || 0;
                const delaySec = (delayMs / 1000).toFixed(1);
                return (
                  <div key={i} className="hum-delays__item">
                    <span className="hum-delays__label">{i + 1}ª msg</span>
                    <div className="hum-slider__control">
                      <input
                        type="range"
                        className="hum-slider__input"
                        min={0}
                        max={60000}
                        step={1000}
                        value={delayMs}
                        onChange={(e) => {
                          const newDelays = [...(config?.message_delays || [])];
                          // Preencher com 0 se necessário
                          while (newDelays.length <= i) newDelays.push(0);
                          newDelays[i] = parseInt(e.target.value);
                          onChange(moduleKey, { ...config, message_delays: newDelays });
                        }}
                      />
                      <span className="hum-slider__value">
                        {delayMs === 0 ? 'Auto' : `${delaySec}s`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <span className="hum-delays__hint">
              "Auto" usa o cálculo inteligente baseado no tamanho da mensagem
            </span>
          </div>
        </div>
      )}

      {/* Natural Emojis settings */}
      {isEnabled && moduleKey === 'natural_emojis' && (
        <div className="hum-module__settings">
          <div className="hum-module__settings-grid hum-module__settings-grid--2col">
            <div className="hum-slider">
              <span className="hum-slider__label">Probabilidade de emoji</span>
              <div className="hum-slider__control">
                <input
                  type="range"
                  className="hum-slider__input"
                  min={0}
                  max={100}
                  value={(config?.probability || 0.3) * 100}
                  onChange={(e) => onChange(moduleKey, { ...config, probability: parseInt(e.target.value) / 100 })}
                />
                <span className="hum-slider__value">{Math.round((config?.probability || 0.3) * 100)}%</span>
              </div>
            </div>
            <div className="hum-slider">
              <span className="hum-slider__label">Máx. emojis por resposta</span>
              <div className="hum-slider__control">
                <input
                  type="range"
                  className="hum-slider__input"
                  min={1}
                  max={4}
                  value={config?.max_per_interaction || 2}
                  onChange={(e) => onChange(moduleKey, { ...config, max_per_interaction: parseInt(e.target.value) })}
                />
                <span className="hum-slider__value">{config?.max_per_interaction || 2}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Casual Writing settings */}
      {isEnabled && moduleKey === 'casual_writing' && (
        <div className="hum-module__settings">
          <label className="hum-checkbox">
            <input
              type="checkbox"
              checked={config?.typo_enabled || false}
              onChange={(e) => onChange(moduleKey, { ...config, typo_enabled: e.target.checked })}
            />
            <span className="hum-checkbox__mark" />
            <span className="hum-checkbox__label">Simular erros de digitação ocasionais</span>
          </label>
          <span className="hum-module__hint">
            Adiciona erros de digitação ocasionais para parecer mais natural (não recomendado para todos os casos)
          </span>
        </div>
      )}
    </div>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const HumanizationConfigTab = () => {
  const { state, updateHumanizationConfig } = useAgentsFlow();
  const { editor } = state;

  const [config, setConfig] = useState(DEFAULT_CONFIG);

  useEffect(() => {
    if (editor?.humanization_config) {
      setConfig({
        ...DEFAULT_CONFIG,
        ...editor.humanization_config,
        modules: {
          ...DEFAULT_CONFIG.modules,
          ...editor.humanization_config?.modules,
        },
      });
    }
  }, [editor?.humanization_config]);

  const handleMainToggle = useCallback((enabled) => {
    const newConfig = { ...config, enabled };
    setConfig(newConfig);
    updateHumanizationConfig(newConfig);
  }, [config, updateHumanizationConfig]);

  const handleModuleChange = useCallback((moduleKey, moduleConfig) => {
    const newConfig = {
      ...config,
      modules: {
        ...config.modules,
        [moduleKey]: moduleConfig,
      },
    };
    setConfig(newConfig);
    updateHumanizationConfig(newConfig);
  }, [config, updateHumanizationConfig]);

  const enabledModulesCount = Object.values(config.modules || {}).filter(m => m.enabled !== false).length;
  const totalModulesCount = Object.keys(MODULE_INFO).length;

  return (
    <div className="hum-config">
      {/* Header */}
      <section className="hum-section">
        <div className="hum-header">
          <div className="hum-header__left">
            <div className={`hum-header__icon ${config.enabled ? 'hum-header__icon--active' : ''}`}>
              <IconSparkles size={24} />
            </div>
            <div className="hum-header__info">
              <h3 className="hum-header__title">Humanização de Conversas</h3>
              <span className="hum-header__desc">
                Transforme respostas robóticas em mensagens naturais estilo WhatsApp
              </span>
            </div>
          </div>
          <div className="hum-header__right">
            <span className="hum-header__status">
              {config.enabled ? 'Ativado' : 'Desativado'}
            </span>
            <label className="hum-toggle">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => handleMainToggle(e.target.checked)}
              />
              <span className="hum-toggle__slider" />
            </label>
          </div>
        </div>

        {config.enabled && (
          <div className="hum-header__meta">
            <span className="hum-counter">
              {enabledModulesCount}/{totalModulesCount} módulos ativos
            </span>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Módulos de Prompt são injetados no prompt da IA. Módulos de Pós-processamento transformam a resposta após a IA responder.</Tooltip>}
            >
              <IconInfoCircle size={14} className="hum-header__info-icon" />
            </OverlayTrigger>
          </div>
        )}
      </section>

      {config.enabled && (
        <>
          {/* Prompt Modules */}
          <section className="hum-section">
            <div className="hum-section__header">
              <IconMessage size={18} />
              <h3 className="hum-section__title">Módulos de Prompt</h3>
            </div>
            <p className="hum-section__desc">
              Estes módulos modificam as instruções da IA para que ela já gere respostas humanizadas desde o início.
            </p>
            <div className="hum-modules">
              {Object.entries(MODULE_INFO)
                .filter(([_, info]) => info.category === 'prompt')
                .map(([key]) => (
                  <ModuleCard
                    key={key}
                    moduleKey={key}
                    config={config.modules?.[key]}
                    onChange={handleModuleChange}
                  />
                ))}
            </div>
          </section>

          {/* Post-processing Modules */}
          <section className="hum-section">
            <div className="hum-section__header">
              <IconSparkles size={18} />
              <h3 className="hum-section__title">Módulos de Pós-processamento</h3>
            </div>
            <p className="hum-section__desc">
              Estes módulos processam a resposta da IA após ela ser gerada, adicionando formatação, emojis e timing natural.
            </p>
            <div className="hum-modules">
              {Object.entries(MODULE_INFO)
                .filter(([_, info]) => info.category === 'post')
                .map(([key]) => (
                  <ModuleCard
                    key={key}
                    moduleKey={key}
                    config={config.modules?.[key]}
                    onChange={handleModuleChange}
                  />
                ))}
            </div>
          </section>
        </>
      )}

      {!config.enabled && (
        <div className="hum-empty">
          <IconSparkles size={48} />
          <h3 className="hum-empty__title">Humanização desativada</h3>
          <span className="hum-empty__desc">
            Ative a humanização para transformar as respostas do seu agente em mensagens mais naturais e humanas.
          </span>
        </div>
      )}
    </div>
  );
};

export default HumanizationConfigTab;
